import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Card, CardContent, Grid2, Tooltip } from "@mui/material";
import {
  GridActionsCellItem,
  GridColDef,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { DataGridPro, DataGridProProps } from "@mui/x-data-grid-pro";
import { format } from "date-fns";
import useApi from "../../../hooks/useApi";
import {
  AUDITRACEACTIONS,
  dataGridHeaderHeight,
  dataGridRowHeight,
  GLOBALSETTINGS,
  GRIDCOLUMNKEYS,
  SEARCHBOX,
  SEVERITIES,
  SYSTEMSETTINGS,
} from "../../../data/constants";
import { useSettingValue } from "../../../services/useSettingValue";
import { useUser } from "../../../context/UserContext";
import {
  filterDataBySearchWithGridFields,
  handleScrollPositionChange,
} from "../../../utils/commonHelper";
import SearchBox from "../../../components/form/SearchBox/SearchBox";
import {
  PortalUserSessionFailedLoginAttemptCountDto,
  PortalUserSessionsDTO,
  PortalUsersSessionLisRequest,
} from "../../../types/PortalUser";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LockIcon from "@mui/icons-material/Lock";
import { MANAGEUSERSESSION } from "../../../utils/Constants";
import ConfirmationDialog from "../../../components/ui/ConfirmationDialog";
import {
  getPortalUserSessionList,
  updatePortalUserSessionFailedLoginAttemptCount,
} from "../../../services/portalUserSessionService";
import { useToaster } from "../../../hooks/useToaster";
import CustomLoadingOverlay from "../../../components/ui/CustomLoadingOverlay";
import { GLOBALMESSAGES } from "../../../constants/messages";
import { logAuditTrace } from "../../../services/auditNavigationTraceService";
import { getCurrentESTTime } from "../../../utils/dateHelper";
import { useResizableColumns } from "../../../hooks/useResizableColumns";
import { usePersistedColumnVisibility } from "../../../hooks/usePersistedColumnVisibility";
interface PortalUserSessionListProps {
  isUserSessionDetailEditPermission: boolean;
}

function PortalUserSessionList(props: PortalUserSessionListProps) {
  const { isUserSessionDetailEditPermission } = props;
  const [searchResult, setSearchResult] = useState("");
  const { loading, executeApi } = useApi();
  const settingValue = useSettingValue(SYSTEMSETTINGS.MANAGESESSIONS_PAGINATION);
  const [isPaginationInitialized, setPaginationInitialized] = useState(false);
  const [pageState, setPageState] = useState<{
    data: PortalUserSessionsDTO[] | null;
    total: number;
  }>({
    data: null,
    total: 0,
  });
  const [isClientSearchEvaluated, setIsClientSearchEvaluated] = useState(false);
  const [allData, setAllData] = useState<PortalUserSessionsDTO[]>([]);
  const isFetching = useRef(false);
  const isFirstLoaded = useRef(false);
  const mounted = React.useRef(true);
  const apiRef = useGridApiRef();
  const { userId } = useUser();
  const showToaster = useToaster();

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: parseInt(settingValue ?? GLOBALSETTINGS.DEFAULT_VALUE, 10),
  });

  React.useEffect(() => {
    apiRef?.current?.subscribeEvent("scrollPositionChange", handleScrollPositionChange);
    return () => {
      mounted.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnRowsScrollEnd: DataGridProProps["onRowsScrollEnd"] = () => {
    if (loading || !pageState?.data) return; // Avoid multiple triggers
    if (isFetching.current) return;

    if (pageState?.data && pageState?.data.length >= pageState?.total) {
      return;
    }

    isFetching.current = true;
    setPaginationModel((prev) => {
      const totalPages = Math.ceil(pageState?.total / prev.pageSize);
      if (prev.page + 1 < totalPages) {
        return { ...prev, page: prev.page + 1 };
      }
      return prev;
    });

    // Restore previous scroll position after data updates
    setTimeout(() => {
      isFetching.current = false;
    }, 100);
  };

  const [state, setState] = useState({
    portalUserSession: {} as PortalUserSessionsDTO,
    isInitialLoad: true,
    lockText: "",
    filterText: "",
    page: 0,
    dialogOpen: false,
    dialogTitleText: "",
  });

  const updateState = (newState: Partial<typeof state>) => {
    setState((prevState) => ({
      ...prevState,
      ...newState,
    }));
  };

  const loadData = useCallback(async () => {
    const isNewSearch = paginationModel.page === 0;
    if (isClientSearchEvaluated) {
      if (searchResult) {
        const lowerText = searchResult.toLowerCase();
        const filtered = filterDataBySearchWithGridFields(allData, lowerText, searchableFields);
        setPageState({
          data: filtered ?? [],
          total: filtered?.length,
        });
      } else {
        // If no search term, reset to full data
        setPageState({
          data: allData ?? [],
          total: allData?.length,
        });
      }
    } else {
      const portalUsersSessionLisRequest: PortalUsersSessionLisRequest = {
        searchTerm: searchResult,
        pageNumber: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
      };
      const portalUsersSessionListData = await executeApi<
        PortalUsersSessionLisRequest,
        PortalUserSessionsDTO[]
      >(getPortalUserSessionList, portalUsersSessionLisRequest);
      portalUsersSessionListData?.sort((a, b) => (a.isLocked < b.isLocked ? 1 : -1));
      if (portalUsersSessionListData && portalUsersSessionListData.length > 0) {
        if (
          !isFirstLoaded.current &&
          portalUsersSessionListData[0]?.totalRecords <= paginationModel.pageSize
        ) {
          setIsClientSearchEvaluated(true);
          setAllData(portalUsersSessionListData);
        }
        isFirstLoaded.current = true;

        setPageState((prev) => {
          const existingIds = new Set(prev.data?.map((row) => row.rowNumber)); // Track existing IDs
          const newData = portalUsersSessionListData.filter(
            (row) => !existingIds.has(row.rowNumber)
          ); // Filter duplicates

          return {
            data: isNewSearch ? portalUsersSessionListData : [...(prev.data || []), ...newData],
            total:
              portalUsersSessionListData.length > 0 && isNewSearch
                ? portalUsersSessionListData[0].totalRecords
                : prev.total,
          };
        });
      } else {
        setPageState({
          data: [],
          total: 0,
        });
      }
    }
    setTimeout(() => {
      apiRef?.current?.scrollToIndexes({ rowIndex: apiRef.current.getScrollPosition().top });
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, paginationModel, searchResult, executeApi]);

  useEffect(() => {
    const newPageSize = parseInt(settingValue ?? GLOBALSETTINGS.DEFAULT_VALUE, 10);

    setPaginationModel((prev) => {
      if (prev.pageSize !== newPageSize) {
        return { page: 0, pageSize: newPageSize };
      }
      return prev;
    });
    setPaginationInitialized(true);
  }, [settingValue]);

  useEffect(() => {
    if (isPaginationInitialized) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaginationInitialized, loadData]);

  const handleSearchText = useCallback((text: string) => {
    setSearchResult((prev) => (prev !== text ? text : prev));
    setPaginationModel((prev) => ({
      ...prev,
      page: 0,
    }));
  }, []);

  const handleEditUserHandler = (userSession: PortalUserSessionsDTO) => {
    updateState({
      lockText: userSession.isLocked ? MANAGEUSERSESSION.UNLOCK : MANAGEUSERSESSION.LOCK,
    });
    updateState({
      dialogTitleText: userSession.isLocked
        ? MANAGEUSERSESSION.UNLOCKDIALOGTITLE
        : MANAGEUSERSESSION.LOCKDIALOGTITLE,
    });
    updateState({ portalUserSession: userSession });
    openConfirmDialog();
  };

  const openConfirmDialog = () => {
    updateState({ dialogOpen: true });
  };

  const handleDialogClose = async (confirmed: boolean) => {
    if (confirmed) {
      handleEditUserSession(state.portalUserSession);
    }
    updateState({ dialogOpen: false });
  };

  const handleEditUserSession = async (userSession: PortalUserSessionsDTO) => {
    userSession.isLocked = userSession.isLocked ? false : true;
    userSession.userId = userId;
    const response = await executeApi<PortalUserSessionFailedLoginAttemptCountDto, string>(
      updatePortalUserSessionFailedLoginAttemptCount,
      userSession
    );
    if (response === MANAGEUSERSESSION.SUCCESS) {
      const userName = state.portalUserSession.userName
        ? " " + state.portalUserSession.userName
        : "";

      const successMsg = MANAGEUSERSESSION.LOCKUSERSESSIONSUCCESSMESSAGE.replace(
        "{0}",
        userName
      ).replace("{1}", state.lockText);
      showToaster(successMsg, SEVERITIES.SUCCESS);
      await loadData();
    }

    let actionDescription = userSession.isLocked
      ? AUDITRACEACTIONS.LOCKSESSION.toString()
      : AUDITRACEACTIONS.UNLOCKSESSION.toString();
    if (response !== MANAGEUSERSESSION.SUCCESS) {
      actionDescription += GLOBALMESSAGES.FAILED_ADDITION;
    }

    logAuditTrace(
      {
        userId: userId,
        objectType: "Session",
        objectId: userSession.sessionId,
        objectName: userSession.sessionValue,
        action: actionDescription,
        navigationDateTime: getCurrentESTTime(),
      },
      null,
      true
    );
  };

  const columns: GridColDef[] = [
    { field: "sessionId", headerName: "Session ID", minWidth: 100, flex: 1 },
    { field: "sessionValue", headerName: "Session Value", minWidth: 200, flex: 1 },
    { field: "userName", headerName: "User Name", minWidth: 200, flex: 1 },
    { field: "roleName", headerName: "Roles", minWidth: 100, flex: 1 },
    {
      field: "lockedOn",
      headerName: "Locked On",
      minWidth: 200,
      flex: 1,
      valueFormatter: (value) => (value ? format(value, GLOBALSETTINGS.DATE_FORMAT) : ""),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.1,
      minWidth: 100,
      hideable: false,
      filterable: false,
      sortable: false,
      disableColumnMenu: false,
      renderCell: (params) => (
        <GridActionsCellItem
          disabled={!isUserSessionDetailEditPermission}
          icon={
            params.row.isLocked === true ? (
              <Tooltip title={MANAGEUSERSESSION.UNLOCKSESSION} placement="top">
                <LockIcon />
              </Tooltip>
            ) : (
              <Tooltip title={MANAGEUSERSESSION.LOCKSESSION} placement="top">
                <LockOpenIcon />
              </Tooltip>
            )
          }
          label="View"
          onClick={() => handleEditUserHandler(params.row)}
        />
      ),
    },
  ];
  const searchableFields = columns.map((col) => col.field).filter((field) => field !== "actions");

    const {
      columnState,
      pinnedColumns,
      setPinnedColumns,
      handleColumnResize,
    } = useResizableColumns(columns);

  const { columnVisibilityModel, setColumnVisibilityModel } = usePersistedColumnVisibility(
    GRIDCOLUMNKEYS.PORTAL_USER_SESSIONS
  );

  return (
    <section className="mb-3">
      <Card variant="outlined">
     <CardContent>
          <Box component="form">
            <Grid2 container spacing={2}>
              <Grid2 size={12}>
                <SearchBox
                  searchPlaceholder={SEARCHBOX.TEXT_PLACEHOLDER}
                  onSearch={handleSearchText}
                />
              </Grid2>
              <Grid2 size={12}>
                <div style={{ height: 500, width: "100%" }}>
                  <DataGridPro
                    apiRef={apiRef}
                    rows={pageState?.data ?? []}
                    {...pageState?.data}
                    columns={columnState}
                    disableMultipleRowSelection
                    rowHeight={dataGridRowHeight}
                    columnHeaderHeight={dataGridHeaderHeight}
                    rowCount={pageState?.total}
                    loading={loading}
                    paginationMode="server"
                    getRowId={(row) => row.rowNumber}
                    slots={{
                      loadingOverlay: CustomLoadingOverlay,
                    }}
                    hideFooterPagination
                    hideFooterSelectedRowCount
                    onRowsScrollEnd={handleOnRowsScrollEnd}
                    localeText={{ noRowsLabel: MANAGEUSERSESSION.NOROWSTEXT }}
                    pinnedColumns={pinnedColumns}
                    onPinnedColumnsChange={(newPinned) => setPinnedColumns(newPinned)}
                    onColumnWidthChange={(params) => handleColumnResize(params.width, params.colDef)}
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={setColumnVisibilityModel}
                  />
                </div>
              </Grid2>
              <Grid2>
                <ConfirmationDialog
                  open={state.dialogOpen}
                  title={state.dialogTitleText}
                  message={MANAGEUSERSESSION.DIALOGSESSIONMESSAGE.replace(
                    "{0}",
                    state.lockText
                  ).replace(
                    "{1}",
                    state.portalUserSession.userName
                      ? state.portalUserSession.userName
                      : MANAGEUSERSESSION.SELECTEDUSER
                  )}
                  onClose={handleDialogClose}
                />
              </Grid2>
            </Grid2>
          </Box>
         </CardContent>
      </Card>
    </section>
  );
}

export default PortalUserSessionList;
